import { Tooltip } from "@mui/material";
import { useMemo } from "react";
import { isValidArray } from "utils/array";

import { convertDateToTimezone, formatDateTime } from "utils/date";
import { getUniqueCodesWithSeparator } from "utils/billingCode.util";
import { Check as CheckIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { textFilter } from "utils/table";
import formatDate from "utils/formatDate";
import { MOUNTAIN_TIME_ZONE } from "static/constants";
import ReportDateHeader from "../ReportDateHeader";

const useArchivedReportTableColumns = () => {
  const columns = useMemo(
    () => [
      {
        header: "Report ID",
        accessorKey: "report_id",
        filterVariant: "text",
        filterFn: textFilter,
        size: 150,
        Cell: ({ row }) => row?.original?._id || "-",
      },
      {
        header: "Account",
        accessorKey: "account",
        filterVariant: "text",
        filterFn: textFilter,
        size: 180,
      },
      {
        header: "Report Date",
        accessorKey: "report_date",
        filterVariant: "date",
        filterFn: textFilter,
        size: 60,
        Header: ReportDateHeader,
        Cell: ({ cell }) =>
          cell?.getValue()
            ? convertDateToTimezone(cell?.getValue(), MOUNTAIN_TIME_ZONE)
            : "-",
      },
      {
        header: "Processed Date",
        accessorKey: "processedOn",
        filterVariant: "date",
        filterFn: textFilter,
        size: 60,
        Cell: ({ cell }) => {
          return cell?.getValue()
            ? formatDate(new Date(cell?.getValue()))
            : "-";
        },
      },
      {
        header: "E-Signature",
        accessorKey: "eSig",
        filterVariant: "boolean",
        filterFn: textFilter,
        size: 60,
        Cell: ({ cell }) => (cell?.getValue() ? <CheckIcon /> : <RemoveIcon />),
      },
      {
        header: "Patient Name",
        accessorKey: "patient_name",
        filterVariant: "text",
        filterFn: textFilter,
        size: 80,
        Cell: ({ row }) => {
          const rowData = row?.original;
          return rowData?.patient?.name || "-";
        },
      },
      {
        header: "Patient Serial Number",
        accessorKey: "patient_serial_num",
        filterVariant: "text",
        filterFn: textFilter,
        size: 150,
        Cell: ({ cell }) => cell?.getValue() || "-",
      },
      {
        header: "Patient DOB",
        accessorKey: "patient_dob",
        filterVariant: "date",
        filterFn: textFilter,
        size: 70,
        Cell: ({ row }) => {
          const rowData = row?.original;
          return rowData?.patient?.dob
            ? formatDateTime(rowData?.patient?.dob, "MM/DD/YYYY")
            : "-";
        },
      },
      {
        header: "Report Billing Date",
        accessorKey: "report_billing_date",
        filterVariant: "date",
        filterFn: textFilter,
        size: 70,
        Cell: ({ row }) =>
          row?.original?.report_billing_date
            ? formatDateTime(row?.original?.report_billing_date, "MM/DD/YYYY")
            : "-",
      },
      {
        header: "Billing Code",
        accessorKey: "billing_code",
        filterVariant: "text",
        size: 140,
        filterFn: textFilter,
        Cell: ({ row }) => {
          const rowData = row?.original;

          return isValidArray(rowData?.billingDiagnosisCodes)
            ? getUniqueCodesWithSeparator(
                rowData?.billingDiagnosisCodes,
                "billingCode"
              )
            : rowData?.billing_code || "-";
        },
      },
      {
        header: "Diagnosis Code",
        accessorKey: "diagnosis_code",
        filterVariant: "text",
        size: 80,
        filterFn: textFilter,
        render: (rowData) => {
          return isValidArray(rowData?.billingDiagnosisCodes)
            ? getUniqueCodesWithSeparator(
                rowData?.billingDiagnosisCodes,
                "diagnosisCode"
              )
            : rowData?.diagnosis_code || "-";
        },
      },
      {
        header: "Physician Name",
        accessorKey: "physician",
        filterVariant: "text",
        filterFn: textFilter,
        // size: 100,
        Cell: ({ row }) => {
          const rowData = row?.original;
          const assignedUser = rowData?.assignedPhysician
            ? [rowData?.assignedPhysician]
            : [];

          const data = [
            ...assignedUser,
            ...(rowData?.patient?.information?.physicians ?? []),
          ];

          if (data?.length < 1) {
            const name = data.length ? data[0].name : "";
            return (
              <div
                className="physician-edit-container"
                style={{ whiteSpace: "nowrap" }}
              >
                <span>{name}</span>
              </div>
            );
          } else {
            const html = data?.map((item, index) => {
              return <div key={index}>{item.name}</div>;
            });

            return (
              <Tooltip title={html} placement="bottom">
                <div
                  className="physician-edit-container"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <span>{data?.[0].name}</span>
                </div>
              </Tooltip>
            );
          }
        },
      },
    ],
    []
  );

  return { columns };
};

export default useArchivedReportTableColumns;
